/**
 * ConfirmDialog — in-app modal for confirms + alerts. Replaces
 * `window.confirm` / `window.alert` so messages stay inside the app
 * shell instead of bouncing through Chrome's own dialog UI.
 *
 * Two render modes:
 *   - mode="confirm" → Confirm + Cancel buttons; resolves boolean.
 *   - mode="alert"   → single OK button; resolves true.
 *
 * Use via the imperative `confirm({ title, message, ... })` and
 * `alertDialog({ title, message })` helpers exported below; they return
 * Promises so existing call sites can `await` them in place of the
 * browser primitives.
 */
import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';

export default function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  danger = false,
  mode = 'confirm',
  onResolve,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onResolve?.(false);
      if (e.key === 'Enter') onResolve?.(true);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onResolve]);

  if (!open) return null;
  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onResolve?.(false);
      }}
    >
      <div className="modal-card confirm-dialog" role="alertdialog" aria-modal="true">
        {title && (
          <div className="modal-header">
            <h2>{title}</h2>
          </div>
        )}
        <div className="modal-body">
          <div className="confirm-dialog-message">{message}</div>
        </div>
        <div className="modal-footer">
          {mode === 'confirm' && (
            <button
              type="button"
              className="modal-btn modal-btn-ghost"
              onClick={() => onResolve?.(false)}
            >{cancelLabel}</button>
          )}
          <button
            type="button"
            className={`modal-btn ${danger ? 'modal-btn-danger' : 'modal-btn-primary'}`}
            onClick={() => onResolve?.(true)}
            autoFocus
          >{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

// Imperative API: mounts a temporary root, renders the dialog,
// resolves the promise on user action, then unmounts.
function showDialog(props) {
  return new Promise((resolve) => {
    const host = document.createElement('div');
    document.body.appendChild(host);
    const root = createRoot(host);
    const cleanup = (value) => {
      root.unmount();
      host.remove();
      resolve(value);
    };
    root.render(
      <ConfirmDialog
        open
        {...props}
        onResolve={cleanup}
      />
    );
  });
}

export function confirm({ title = 'Are you sure?', message, confirmLabel, cancelLabel, danger } = {}) {
  return showDialog({ title, message, confirmLabel, cancelLabel, danger, mode: 'confirm' });
}

export function alertDialog({ title = '', message, confirmLabel = 'OK' } = {}) {
  return showDialog({ title, message, confirmLabel, mode: 'alert' });
}
